/**
 * Centralized date utilities for booking system
 *
 * This module provides consistent date formatting and manipulation
 * across the application with support for French localization.
 */

// ============================================================================
// Constants
// ============================================================================

/** Default locale for date formatting */
export const DATE_LOCALE = "fr-FR" as const;

/** Common date format options */
export const DATE_FORMAT_OPTIONS = {
  /** Full date: "Jeudi 9 Octobre 2025" */
  full: {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  } as Intl.DateTimeFormatOptions,

  /** Short date: "9 Oct. 2025" */
  short: {
    day: "numeric",
    month: "short",
    year: "numeric",
  } as Intl.DateTimeFormatOptions,

  /** Medium date: "9 Octobre 2025" */
  medium: {
    day: "numeric",
    month: "long",
    year: "numeric",
  } as Intl.DateTimeFormatOptions,

  /** Numeric date: "09/10/2025" */
  numeric: {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  } as Intl.DateTimeFormatOptions,

  /** Month and year only: "Octobre 2025" */
  monthYear: {
    month: "long",
    year: "numeric",
  } as Intl.DateTimeFormatOptions,

  /** Time only: "14:30" */
  time: {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  } as Intl.DateTimeFormatOptions,

  /** Date with time: "9 Oct. 2025 14:30" */
  dateTime: {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  } as Intl.DateTimeFormatOptions,
} as const;

// ============================================================================
// Core Date Functions
// ============================================================================

/**
 * Format date as ISO string (YYYY-MM-DD) in local timezone
 */
export function toLocalISODate(date: Date | string | null): string | null {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Get current date as ISO string
 */
export function getCurrentDateISO(): string {
  return toLocalISODate(new Date()) || "";
}

/**
 * Parse ISO date string as local date (avoiding UTC conversion)
 */
export function fromLocalISODate(isoString: string): Date {
  const parts = isoString.split("-");
  const year = Number(parts[0] || 0);
  const month = Number(parts[1] || 1);
  const day = Number(parts[2] || 1);
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Add days to a date
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Calculate return date based on departure and trip duration
 */
export function calculateReturnDate(
  departureDate: Date | string | null,
  tripDays: number | null,
): Date | null {
  if (!departureDate || !tripDays) return null;
  const dep =
    typeof departureDate === "string"
      ? fromLocalISODate(departureDate)
      : departureDate;
  return addDays(dep, tripDays - 1);
}

/**
 * Format date for display in French with full month (e.g., "Jeudi 9 Octobre 2025")
 * Capitalizes the first letter of weekday and month
 */
export function formatDateFR(
  date: Date | string | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const defaultOptions: Intl.DateTimeFormatOptions = DATE_FORMAT_OPTIONS.full;
  const formatted = d.toLocaleDateString(DATE_LOCALE, {
    ...defaultOptions,
    ...options,
  });

  // Capitalize first letter of weekday and month
  // Format: "jeudi 9 octobre 2025" -> "Jeudi 9 Octobre 2025"
  return capitalizeWords(formatted);
}

/**
 * Format date for display in French with short month (e.g., "Jeudi 9 Oct. 2025")
 * Capitalizes the first letter of weekday and month
 */
export function formatDateShortFR(
  date: Date | string | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: "long",
    ...DATE_FORMAT_OPTIONS.short,
  };
  const formatted = d.toLocaleDateString(DATE_LOCALE, {
    ...defaultOptions,
    ...options,
  });

  // Capitalize first letter of weekday and month
  return capitalizeWords(formatted);
}

// ============================================================================
// Additional Formatting Functions
// ============================================================================

/**
 * Format date range (e.g., "9 - 15 Octobre 2025" or "28 Sept. - 5 Oct. 2025")
 */
export function formatDateRange(
  startDate: Date | string | null,
  endDate: Date | string | null,
  options: { short?: boolean } = {},
): string {
  if (!startDate || !endDate) return "";

  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  const sameMonth =
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();

  const monthOption = options.short ? "short" : "long";

  if (sameMonth) {
    // Same month: "9 - 15 Octobre 2025"
    const monthYear = start.toLocaleDateString(DATE_LOCALE, {
      month: monthOption,
      year: "numeric",
    });
    return `${start.getDate()} - ${end.getDate()} ${capitalizeWords(monthYear)}`;
  }

  if (sameYear) {
    // Same year: "28 Sept. - 5 Oct. 2025"
    const startStr = start.toLocaleDateString(DATE_LOCALE, {
      day: "numeric",
      month: monthOption,
    });
    const endStr = end.toLocaleDateString(DATE_LOCALE, {
      day: "numeric",
      month: monthOption,
      year: "numeric",
    });
    return `${capitalizeWords(startStr)} - ${capitalizeWords(endStr)}`;
  }

  // Different years: "28 Dec. 2024 - 5 Jan. 2025"
  const format = options.short
    ? DATE_FORMAT_OPTIONS.short
    : DATE_FORMAT_OPTIONS.medium;
  const startStr = start.toLocaleDateString(DATE_LOCALE, format);
  const endStr = end.toLocaleDateString(DATE_LOCALE, format);
  return `${capitalizeWords(startStr)} - ${capitalizeWords(endStr)}`;
}

/**
 * Format relative time (e.g., "il y a 2 jours", "dans 3 heures")
 */
export function formatRelativeTime(date: Date | string | null): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffSecs = Math.round(diffMs / 1000);
  const diffMins = Math.round(diffSecs / 60);
  const diffHours = Math.round(diffMins / 60);
  const diffDays = Math.round(diffHours / 24);

  const rtf = new Intl.RelativeTimeFormat(DATE_LOCALE, { numeric: "auto" });

  if (Math.abs(diffDays) >= 30) {
    return formatDateFR(d, DATE_FORMAT_OPTIONS.medium);
  }
  if (Math.abs(diffDays) >= 1) {
    return rtf.format(diffDays, "day");
  }
  if (Math.abs(diffHours) >= 1) {
    return rtf.format(diffHours, "hour");
  }
  if (Math.abs(diffMins) >= 1) {
    return rtf.format(diffMins, "minute");
  }
  return rtf.format(diffSecs, "second");
}

/**
 * Format duration in days (e.g., "8 jours / 7 nuitées")
 */
export function formatTripDuration(days: number): string {
  if (days <= 0) return "";
  const nights = days - 1;
  return `${days} jour${days > 1 ? "s" : ""} / ${nights} nuitée${nights > 1 ? "s" : ""}`;
}

/**
 * Format time only (e.g., "14:30")
 */
export function formatTime(date: Date | string | null): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleTimeString(DATE_LOCALE, DATE_FORMAT_OPTIONS.time);
}

/**
 * Format date with time (e.g., "9 Oct. 2025 à 14:30")
 */
export function formatDateTime(date: Date | string | null): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const dateStr = d.toLocaleDateString(DATE_LOCALE, DATE_FORMAT_OPTIONS.short);
  const timeStr = d.toLocaleTimeString(DATE_LOCALE, DATE_FORMAT_OPTIONS.time);
  return `${capitalizeWords(dateStr)} à ${timeStr}`;
}

// ============================================================================
// Date Comparison and Validation
// ============================================================================

/**
 * Check if a date is in the past
 */
export function isPastDate(date: Date | string | null): boolean {
  if (!date) return false;
  const d = typeof date === "string" ? new Date(date) : date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

/**
 * Check if a date is today
 */
export function isToday(date: Date | string | null): boolean {
  if (!date) return false;
  const d = typeof date === "string" ? new Date(date) : date;
  const today = new Date();
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
}

/**
 * Get the number of days between two dates
 */
export function getDaysBetween(
  startDate: Date | string | null,
  endDate: Date | string | null,
): number {
  if (!startDate || !endDate) return 0;
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get days until a date (negative if in past)
 */
export function getDaysUntil(date: Date | string | null): number {
  if (!date) return 0;
  const d = typeof date === "string" ? new Date(date) : date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diffTime = d.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Capitalize first letter of each word in a date string
 * (French weekdays and months are lowercase by default)
 */
function capitalizeWords(str: string): string {
  return str
    .split(" ")
    .map((word, index) => {
      // Capitalize weekday (first word) and month (usually third word)
      if (index === 0 || (index === 2 && isNaN(Number(word)))) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    })
    .join(" ");
}

// ============================================================================
// Hijri (Islamic) Calendar
// ============================================================================

/**
 * Convert a Gregorian date to its Hijri (Islamic) year.
 *
 * Uses the Intl API (islamic-umalqura then islamic as fallback) which is
 * accurate and available in Node.js 18+ and all modern browsers.
 * Falls back to a linear approximation if the locale extension is unavailable.
 *
 * @param date - A Gregorian Date object or ISO date string (YYYY-MM-DD).
 * @returns The corresponding Hijri year (e.g. 1447).
 */
export function gregorianToHijriYear(date: Date | string): number {
  const d = typeof date === "string" ? new Date(date) : date;

  // Try Um Al-Qura (Saudi official) calendar first, then generic tabular Islamic
  for (const cal of ["islamic-umalqura", "islamic", "islamic-civil"] as const) {
    try {
      const parts = new Intl.DateTimeFormat(`en-u-ca-${cal}`, {
        year: "numeric",
      }).formatToParts(d);
      const yearPart = parts.find((p) => p.type === "year");
      if (yearPart) return parseInt(yearPart.value, 10);
    } catch {
      // try next variant
    }
  }

  // Last-resort linear approximation (accurate to ±1 year)
  const fractional =
    d.getUTCFullYear() + (d.getUTCMonth() * 30.5 + d.getUTCDate()) / 365.25;
  return Math.floor((fractional - 622) * 1.0307);
}
